import { FC, memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { NextRouter, useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import Grid from '@material-ui/core/Grid';

import { Loader, LoaderSize } from '@keaze/web/components/Loader';
import { GoogleSearchAutocompleteProps } from '@keaze/web/components/googleSearchAutocomplete';
import { Facet } from '@keaze/web/common/interfaces';
import { SearchRadiusProps } from './desktop/searchRadius';
import { MaxPriceProps } from './desktop/maxPrice';
import { MaxSpendProps } from './desktop/maxSpend';
import { BedroomsProps } from './desktop/bedrooms';
import { PropertyTypeProps } from './desktop/propertyType';
import { ParkingProps } from './desktop/parking';
import { MoreProps } from './desktop/more';
import {
  FilterData,
  FilterOnChange,
  FilterValue,
} from '@keaze/web/components/filters';
import {
  SearchFilters,
  FiltersState,
  MultiFilters,
  FilterProps,
  MobileFiltersData,
  OnFiltersChange,
} from './filters.types';
import {
  DesktopFilters,
  StyledMobileFilters,
  Container,
} from './filters.styles';
import {
  useSearchQueryParams,
  UpdateParam,
} from '../hooks/useSearchQueryParams';
import {
  OccupancyType,
  QueryParams,
  SearchParamsForListing,
} from '@keaze/web/common/enums';
import { MobileFilters } from './mobile';
import { generateSearchRadius } from './helpers/generateSearchRadius';
import { FilterSkeleton } from './skeleton';
import { useStateFilters, useUpdateFiltersParams } from './hooks';

const DynamicGoogleSearchAutocomplete = dynamic<GoogleSearchAutocompleteProps>(
  () =>
    import('@keaze/web/components/googleSearchAutocomplete').then(
      (model) => model.GoogleSearchAutocomplete
    ),
  { ssr: false, loading: () => <FilterSkeleton /> }
);

const DynamicSearchRadius = dynamic<SearchRadiusProps>(
  () => import('./desktop/searchRadius').then((model) => model.SearchRadius),
  { ssr: false, loading: () => <FilterSkeleton /> }
);

const DynamicMaxPrice = dynamic<MaxPriceProps>(
  () => import('./desktop/maxPrice').then((model) => model.MaxPrice),
  { ssr: false, loading: () => <FilterSkeleton /> }
);

const DynamicMaxSpend = dynamic<MaxSpendProps>(
  () => import('./desktop/maxSpend').then((model) => model.MaxSpend),
  { ssr: false, loading: () => <FilterSkeleton /> }
);

const DynamicBedrooms = dynamic<BedroomsProps>(
  () => import('./desktop/bedrooms').then((model) => model.Bedrooms),
  { ssr: false, loading: () => <FilterSkeleton /> }
);

const DynamicPropertyType = dynamic<PropertyTypeProps>(
  () => import('./desktop/propertyType').then((model) => model.PropertyType),
  { ssr: false, loading: () => <FilterSkeleton /> }
);

// const DynamicParking = dynamic<ParkingProps>(
//   () => import('./desktop/parking').then((model) => model.Parking),
//   { ssr: false, loading: () => <FilterSkeleton /> }
// );

const DynamicMore = dynamic<MoreProps>(
  () => import('./desktop/more').then((model) => model.More),
  { ssr: false, loading: () => <FilterSkeleton /> }
);

export const DEFAULT_FILTERS_STATE: FiltersState = {
  [SearchFilters.OccupancyType]: null,
  [SearchFilters.Location]: '',
  [SearchFilters.Radius]: null,
  [SearchFilters.MaxPrice]: null,
  [SearchFilters.MaxSpend]: null,
  [SearchFilters.Bedrooms]: [],
  [SearchFilters.PropertyType]: [],
  [SearchFilters.Schemes]: [],
  [SearchFilters.Status]: [],
  [SearchFilters.NewHomes]: [],
  [SearchFilters.Bathrooms]: [],
  [SearchFilters.Parking]: [],
  [SearchFilters.OtherFeatures]: [],
  [SearchFilters.MyDepositBudget]: null,
  [SearchFilters.MaxMonthlyRent]: null,
};

type UpdateFiltersState = (name: string, value: any) => void;

type CheckNullForSelect = (filter: SearchFilters) => number[];

type CheckSelectedValue = (
  currentValue: number[] | number | string | null,
  data: FilterData[]
) => number[] | number | string | null;

type GenerateFiltersByFacets = (
  facets: Facet[],
  data: FiltersState
) => MobileFiltersData[];

type HandleFilterFactoryExpected = {
  [SearchFilters.Location]: () => void;
  [SearchFilters.Radius]: () => void;
};

type HandleFilterFactoryArgs = {
  value: FilterValue;
  router: NextRouter;
  updateParam: UpdateParam;
};

type HandleFilterFactory = (
  args: HandleFilterFactoryArgs
) => HandleFilterFactoryExpected;

const checkSelectedValue: CheckSelectedValue = (currentValue, data) => {
  if (currentValue === null) {
    return null;
  }

  if (Array.isArray(currentValue)) {
    return currentValue.reduce<number[]>((acc, selectedValue) => {
      const existingValue = data.find(({ value }) => value === selectedValue);

      if (existingValue && existingValue.count !== 0) {
        return [...acc, selectedValue];
      }

      return acc;
    }, []);
  }

  const existingValue = data.find(({ value }) => value == currentValue);

  return existingValue && existingValue.count !== 0 ? currentValue : null;
};

const generateFiltersByFacets: GenerateFiltersByFacets = (facets, filters) =>
  facets.map((facet) => ({
    ...facet,
    value: checkSelectedValue(filters[facet.param], facet.options),
  }));

const handleFilterFactory: HandleFilterFactory = ({
  value,
  router,
  updateParam,
}) => ({
  [SearchFilters.Location]: () =>
    updateParam({
      param: QueryParams.Location,
      value: value as string,
      router,
    }),
  [SearchFilters.Radius]: () =>
    updateParam({ param: QueryParams.Radius, value: value as string, router }),
  [SearchFilters.Bedrooms]: () =>
    updateParam({
      param: SearchParamsForListing.Bedrooms,
      value: value as string,
      router,
    }),
});

export const Filters: FC<FilterProps> = memo(
  ({
    facets,
    isLoading,
    withoutParams = false,
    withOnlyLocationData = false,
    className,
    onChange,
  }) => {
    const router = useRouter();
    const { type: occupancyType } = useSearchQueryParams();
    const prevOccupancyType = useRef(occupancyType);
    const {
      filters,
      setFilters,
      updateStoredFilters,
      getStoredFiltersByType,
    } = useStateFilters(withOnlyLocationData);
    const generatedFiltersByFacets = useMemo(
      () => generateFiltersByFacets(facets, filters),
      [facets, filters]
    );
    const { updateParam } = useSearchQueryParams();
    useUpdateFiltersParams(filters);

    const checkNullForSelect: CheckNullForSelect = useCallback(
      (filter) =>
        filters[filter] === null ? [] : (filters[filter] as number[]),
      [filters]
    );

    const mobileRadiusProps = useMemo(
      () => ({
        data: generateSearchRadius(),
        value: checkNullForSelect(SearchFilters.Radius),
      }),
      [checkNullForSelect]
    );

    const moreData = useMemo(
      () =>
        generatedFiltersByFacets.filter(
          ({ param }) =>
            param !== SearchFilters.MaxSpend &&
            param !== SearchFilters.Bedrooms &&
            param !== SearchFilters.PropertyType
        ),
      [generatedFiltersByFacets]
    );

    const isMoreFilled = useMemo(
      () =>
        checkNullForSelect(SearchFilters.MyDepositBudget).length !== 0 ||
        checkNullForSelect(SearchFilters.MaxMonthlyRent).length !== 0 ||
        checkNullForSelect(SearchFilters.Schemes).length !== 0 ||
        checkNullForSelect(SearchFilters.Status).length !== 0 ||
        checkNullForSelect(SearchFilters.NewHomes).length !== 0 ||
        checkNullForSelect(SearchFilters.Bathrooms).length !== 0 ||
        checkNullForSelect(SearchFilters.Parking).length !== 0 ||
        checkNullForSelect(SearchFilters.OtherFeatures).length !== 0,
      [checkNullForSelect]
    );

    const handleFiltersChange: OnFiltersChange = useCallback(
      (filters) => {
        setFilters(filters);
        onChange(filters);
      },
      [onChange, setFilters]
    );

    useEffect(() => {
      updateStoredFilters(filters);
    }, [filters]);

    useEffect(() => {
      if (prevOccupancyType.current !== occupancyType) {
        prevOccupancyType.current = occupancyType;

        handleFiltersChange(
          getStoredFiltersByType(occupancyType as OccupancyType)
        );
      }
    }, [
      prevOccupancyType,
      occupancyType,
      getStoredFiltersByType,
      handleFiltersChange,
    ]);

    useEffect(() => {
      setFilters((prevState) => {
        const filtersByFacets = generateFiltersByFacets(facets, prevState);
        const updatedFilters = filtersByFacets.reduce(
          (acc, { param, value }) => ({
            ...acc,
            [param]: value,
          }),
          {}
        );

        return {
          ...prevState,
          ...updatedFilters,
        };
      });
    }, [facets, setFilters]);

    const getFacetByParam = useCallback(
      (param: SearchFilters) => {
        const facet = facets.find((facet) => facet.param === param);

        return facet ? facet.options : [];
      },
      [facets]
    );

    const updateFiltersState: UpdateFiltersState = useCallback(
      (name, value) => {
        const isMultiFilters =
          name === MultiFilters.More || name === MultiFilters.Mobile;

        if (isMultiFilters && Array.isArray(value)) {
          const defaultFiltersState = value.reduce(
            (acc, filter) => ({
              ...acc,
              [filter]: DEFAULT_FILTERS_STATE[filter],
            }),
            {}
          );

          return handleFiltersChange({
            ...filters,
            ...defaultFiltersState,
          });
        }

        return handleFiltersChange({
          ...filters,
          [name]: value,
        });
      },
      [filters, handleFiltersChange]
    );

    const handleChange: FilterOnChange = useCallback(
      (name, value) => {
        updateFiltersState(name, value);
        localStorage.removeItem('monthlyCost');

        const handleFactory = handleFilterFactory({
          value,
          router,
          updateParam,
        });
        if (handleFactory[name] && !withoutParams) {
          handleFactory[name]();
        }
      },
      [router, updateFiltersState, withoutParams, updateParam]
    );

    return (
      <>
        <DesktopFilters className={className}>
          <Container container>
            <Grid item xs={12} md={12} lg={3}>
              <DynamicGoogleSearchAutocomplete
                withIcon
                value={filters[SearchFilters.Location] as string}
                onChange={(value) =>
                  handleChange(SearchFilters.Location, value)
                }
              />
            </Grid>
            <Grid item xs className="radiusFilter">
              <DynamicSearchRadius
                isLoading={isLoading}
                value={filters[SearchFilters.Radius]}
                data={generateSearchRadius()}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs>
              <DynamicMaxSpend
                isLoading={isLoading}
                value={filters[SearchFilters.MaxSpend]}
                data={getFacetByParam(SearchFilters.MaxSpend)}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs>
              <DynamicBedrooms
                isLoading={isLoading}
                value={filters[SearchFilters.Bedrooms] as number[]}
                data={getFacetByParam(SearchFilters.Bedrooms)}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs>
              <DynamicPropertyType
                isLoading={isLoading}
                value={filters[SearchFilters.PropertyType] as number[]}
                data={getFacetByParam(SearchFilters.PropertyType)}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs>
              {/* <DynamicParking
                isLoading={isLoading}
                value={filters[SearchFilters.ParkingType] as number[]}
                data={getFacetByParam(SearchFilters.ParkingType)}
                onChange={handleChange}
              /> */}
              {/* <DynamicMore
                isFilled={isMoreFilled}
                data={moreData}
                onChange={handleChange}
              /> */}
            </Grid>
          </Container>
          <Loader open={isLoading} size={LoaderSize.Small} />
        </DesktopFilters>

        <StyledMobileFilters className={className}>
          <MobileFilters
            googleSearchValue={filters[SearchFilters.Location] as string}
            radius={mobileRadiusProps}
            data={generatedFiltersByFacets}
            isLoading={isLoading}
            onChange={handleChange}
          />
        </StyledMobileFilters>
      </>
    );
  }
);
